/**
 * Web Crypto API utilities for local hash generation
 * Privacy-by-Design: Hash is computed locally before sending to server
 */

/**
 * Convert ArrayBuffer to hexadecimal string
 */
function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Compute SHA-256 hash of a file locally using Web Crypto API
 *
 * @param file - File to hash
 * @returns Promise with the hex-encoded SHA-256 hash
 *
 * @example
 * const hash = await computeFileSHA256(pdfFile)
 * console.log(hash) // "a1b2c3d4..."
 */
export async function computeFileSHA256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  return bufferToHex(hashBuffer)
}

/**
 * Compute SHA-256 hash of a string
 */
export async function computeStringSHA256(data: string): Promise<string> {
  const encoder = new TextEncoder()
  const dataBuffer = encoder.encode(data)
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
  return bufferToHex(hashBuffer)
}

/**
 * Validate if browser supports Web Crypto API
 */
export function isCryptoSupported(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined'
}
